import { clearLandingMetadata } from './pages/landing/landingSeo';
import "@/App.css";
import "@/components/ui/ui_brand.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BuyerModeProvider } from "./context/BuyerModeProvider";
import SeanEllisModal from "./components/SeanEllisModal";
import { ProtectedRoute } from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import { LoadingScreen } from "./components/ui/LoadingScreen";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { observeSiteSession } from "./lib/analytics";
import { applyTheme } from "./lib/theme";

// Pages. Eager: the landing and the pages prerendered with it (scripts/prerenderLanding.ts).
// Everything else loads on first visit, so the public pages do not download the buyer app (mid-9cac.3)
// and a buyer does not download the admin, agent and intermediary apps (mid-e98.5).
import LandingRoute from "./pages/landing/LandingRoute";
import AgentPricingPublicPage from "./pages/AgentPricingPublicPage";
import GuidePage from "./pages/guides/GuidePage";
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const BuyerDNAOnboardingV2 = lazy(() => import("./pages/BuyerDNAOnboardingV2"));
const BuyerDNAReportPage = lazy(() => import("./pages/BuyerDNAReportPage"));
const TradeOffSwiperPage = lazy(() => import("./pages/TradeOffSwiperPage"));
// LifestyleQuestionnairePage removed Fev 2026, flow merged into BuyerDNAOnboardingV2.
const AgentOnboarding = lazy(() => import("./pages/AgentOnboarding"));
const BuyerAppV3 = lazy(() => import("./pages/BuyerAppV3"));
const PropertyDetailV3 = lazy(() => import("./pages/PropertyDetailV3"));
// Support chat (mid-oitb.7): one panel for every /buyer/* screen but the DNA chat. Lazy, so the
// public pages do not download it.
const BuyerSupportLayout = lazy(() => import("./components/support/BuyerSupportLayout"));
const FloatingSupportBubble = lazy(() => import("./components/support/BuyerSupportLayout").then((m) => ({ default: m.FloatingSupportBubble })));
const AgentDashboard = lazy(() => import("./pages/AgentDashboard"));
const BuyerProfilePage = lazy(() => import("./pages/BuyerProfilePage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const DemoOutboxPage = lazy(() => import("./pages/DemoOutboxPage"));
const AdminCreditIntermediaries = lazy(() => import("./pages/AdminCreditIntermediaries"));
const RegisterCreditIntermediary = lazy(() => import("./pages/RegisterCreditIntermediary"));
const CompleteCreditIntermediaryRegistration = lazy(() => import("./pages/CompleteCreditIntermediaryRegistration"));
const IntermediaryLogin = lazy(() => import("./pages/IntermediaryLogin"));
const IntermediaryDashboard = lazy(() => import("./pages/IntermediaryDashboard"));
const IntermediarySetupPassword = lazy(() => import("./pages/IntermediarySetupPassword"));
const IntermediaryForgotPassword = lazy(() => import("./pages/IntermediaryForgotPassword"));
const IntermediaryResetPassword = lazy(() => import("./pages/IntermediaryResetPassword"));
const IntermediaryPublicProfile = lazy(() => import("./pages/IntermediaryPublicProfile"));
const SharedPropertyPage = lazy(() => import("./pages/SharedPropertyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AgentSubscribePage = lazy(() => import("./pages/AgentSubscribePage"));
import PrivacyPage from "./pages/PrivacyPage";
import CookiesPage from "./pages/CookiesPage";
import NotFoundPage from "./pages/NotFoundPage";

import ErrorBoundary from "./components/ErrorBoundary";

function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    if (!['/', '/sobre', '/precos-agentes', '/privacy', '/cookies'].includes(location.pathname) && !location.pathname.startsWith('/guias/')) clearLandingMetadata();
  }, [location.pathname]);
  useEffect(() => { applyTheme(); }, [location.pathname]); // pinned routes (lib/theme.js) switch on client navigation
  const { analyticsRole, analyticsUserId, loading } = useAuth();

  useEffect(() => {
    if (!loading) {
      const actor = localStorage.getItem('intermediary_token') ? null : analyticsRole;
      observeSiteSession(actor, actor === 'buyer' ? analyticsUserId : null);
    }
  }, [analyticsRole, analyticsUserId, loading]);

  return (
    <>
    <ScrollToTop />
    <Suspense fallback={<LoadingScreen />}>
    {/* Rental mode (mid-uhy.24): one mode context for every buyer route, not only the cockpit. */}
    <BuyerModeProvider>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingRoute />} />
      {/* Public onboarding (onep.19): the same page as /buyer/onboarding/v2; it resolves the session itself. */}
      <Route path="/comecar" element={<BuyerDNAOnboardingV2 />} />
      <Route path="/sobre" element={<LandingRoute about />} />
      <Route path="/precos-agentes" element={<AgentPricingPublicPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/legal/termos" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/legal/privacidade" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/guias/:slug" element={<GuidePage />} />

      {/* Credit Intermediary Registration */}
      <Route path="/register-credit-intermediary" element={<RegisterCreditIntermediary />} />
      <Route path="/intermediarios-credito/registo" element={<RegisterCreditIntermediary />} />
      <Route path="/complete-credit-intermediary-registration" element={<CompleteCreditIntermediaryRegistration />} />
      
      {/* Credit Intermediary Portal */}
      <Route path="/intermediary/login" element={<IntermediaryLogin />} />
      <Route path="/intermediary/setup-password" element={<IntermediarySetupPassword />} />
      <Route path="/intermediary/forgot-password" element={<IntermediaryForgotPassword />} />
      <Route path="/intermediary/reset-password" element={<IntermediaryResetPassword />} />
      <Route path="/intermediary-dashboard" element={<IntermediaryDashboard />} />
      <Route path="/intermediary/:intermediaryId" element={<IntermediaryPublicProfile />} />
      
      {/* Shared Property (Public) */}
      <Route path="/property/:shareToken" element={<SharedPropertyPage />} />

      {/* Buyer Routes */}
      <Route
        path="/buyer/onboarding"
        element={
          <ProtectedRoute allowedUserTypes={['buyer']}>
            <BuyerDNAOnboardingV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyer/onboarding/v2"
        element={
          <ProtectedRoute allowedUserTypes={['buyer']}>
            <BuyerDNAOnboardingV2 />
          </ProtectedRoute>
        }
      />
      {/* /buyer/lifestyle route removed (Fev 2026): the lifestyle extraction
          flow now lives inline inside the main Buyer DNA form, with the
          reveal animation playing right before the moodboard step.
          Redirect any stale bookmarks / inbound links to the dashboard. */}
      <Route path="/buyer/lifestyle" element={<Navigate to="/buyer/dashboard" replace />} />
      {/* Every buyer screen with the support bubble (mid-oitb.7); the DNA chat routes above have none. */}
      <Route element={<BuyerSupportLayout />}>
        <Route
          path="/buyer/dashboard"
          element={
            <ProtectedRoute allowedUserTypes={['buyer']}>
              <BuyerAppV3 />
            </ProtectedRoute>
          }
        />
        <Route element={<FloatingSupportBubble overBottomNav />}>
          <Route
            path="/buyer/property/:propertyId"
            element={
              <ProtectedRoute allowedUserTypes={['buyer']}>
                <PropertyDetailV3 />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route element={<FloatingSupportBubble />}>
          <Route
            path="/buyer/dna-report"
            element={
              <ProtectedRoute allowedUserTypes={['buyer']}>
                <BuyerDNAReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/tradeoffs"
            element={
              <ProtectedRoute allowedUserTypes={['buyer']}>
                <TradeOffSwiperPage />
              </ProtectedRoute>
            }
          />
        </Route>
      </Route>
      {/* v1 buyer screens retired (UX-04, 2026-10). Old bookmarks land on the cockpit. */}
      <Route path="/buyer/onboarding/legacy" element={<Navigate to="/buyer/dashboard" replace />} />
      <Route path="/buyer/dashboard/legacy" element={<Navigate to="/buyer/dashboard" replace />} />

      {/* Agent Routes */}
      <Route
        path="/agent/onboarding"
        element={
          <ProtectedRoute allowedUserTypes={['agent']}>
            <AgentOnboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/dashboard"
        element={
          <ProtectedRoute allowedUserTypes={['agent']}>
            <AgentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/buyers/:buyerId"
        element={
          <ProtectedRoute allowedUserTypes={['agent']}>
            <BuyerProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/subscribe"
        element={
          <ProtectedRoute allowedUserTypes={['agent']}>
            <AgentSubscribePage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedUserTypes={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/demo/outbox" element={<DemoOutboxPage />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedUserTypes={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/credit-intermediaries"
        element={
          <ProtectedRoute allowedUserTypes={['admin']}>
            <AdminCreditIntermediaries />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </BuyerModeProvider>
    </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <CookieConsentBanner />
            <SeanEllisModal />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
